// src/components/pat/PatResultPanel.jsx
// Post-confirmation PAT results (stable, adjudicative view)

import React, { useMemo, useState } from "react";
import jsPDF from "jspdf";
import PATEvidencePanel from "./PATEvidencePanel";
import PatActionLegitimacyPanel from "./PatActionLegitimacyPanel";
import PatIllustrativePathwaysPanel from "./PatIllustrativePathwaysPanel";

function formatProb(value) {
  if (typeof value !== "number" || Number.isNaN(value)) return "–";
  return value.toFixed(2);
}

function pdfValuePreserveLines(value) {
  if (value === null || value === undefined) return "—";

  if (Array.isArray(value)) {
    const cleaned = value
      .map((item) => pdfValuePreserveLines(item))
      .filter((item) => item && item !== "—");
    return cleaned.length ? cleaned.join("\n") : "—";
  }

  if (typeof value === "object") {
    return JSON.stringify(value, null, 2);
  }

  const text = String(value)
    .replace(/\u00a0/g, " ")
    .replace(/[→➙⇒]/g, "->")
    .replace(/[←⇐]/g, "<-")
    .replace(/[“”]/g, '"')
    .replace(/[‘’]/g, "'")
    .replace(/[–—]/g, "-")
    .replace(/…/g, "...")
    .replace(/[^\x09\x0A\x0D\x20-\x7E]/g, "")
    .split(/\r?\n/)
    .map((line) => line.replace(/[ \t]+/g, " ").trim())
    .join("\n")
    .replace(/\n{3,}/g, "\n\n")
    .trim();

  return text || "—";
}

function InfoFlag({ label }) {
  const [open, setOpen] = useState(false);

  if (!label) return null;

  return (
    <span
      className="relative ml-2 inline-flex align-middle"
      onMouseEnter={() => setOpen(true)}
      onMouseLeave={() => setOpen(false)}
    >
      <span
        aria-hidden="true"
        className="inline-flex cursor-help items-center justify-center text-slate-400 hover:text-sky-300"
      >
        ⓘ
      </span>

      {open && (
        <span
          role="tooltip"
          className="absolute left-1/2 top-full z-[9999] mt-1 w-max max-w-xs -translate-x-1/2 rounded border border-slate-700 bg-black px-3 py-2 text-xs leading-snug text-slate-200 shadow-lg"
        >
          {label}
        </span>
      )}
    </span>
  );
}

function mediatedPathwayInfoText() {
  return "This corresponds to an authorised mediated/pathway structure in the confirmed DAG. In PAT, these are M variables, not Z/confounder variables. This is a structural graph interpretation, not a Pearl front-door identification result unless the relevant front-door conditions are separately satisfied.";
}

function adjustmentInfoText() {
  return "This corresponds to a back-door/common-cause adjustment structure when the confirmed DAG places a Z variable as a common cause of X and Y. Adjustment is licensed by the confirmed DAG structure, not merely by listing a variable as Z.";
}

function colliderInfoText() {
  return "This corresponds to a collider or collider-risk structure in the confirmed DAG. Conditioning on a collider may open rather than block a path, so it should not be treated as an adjustment variable without justification.";
}

function directInfoText() {
  return "The confirmed DAG contains a direct X → Y structure without a required mediator or candidate adjustment set. This is a structural graph interpretation, not empirical evidence of effect size or real-world success.";
}

function bcsmCalibrationNoteText() {
  return "Calibration note: BCSM is a provisional stability estimate. Its numerical value should be interpreted as a domain-calibrated indicator only once relevant subject-matter expert (SME) thresholds have been established. Until such calibration exists, the score is illustrative and should be interpreted primarily through its stability band and accompanying rationale rather than as a precise quantitative measurement. BCSM provides a structured framework for assessing causal stability; domain-specific evidentiary standards determine how that framework should ultimately be calibrated and applied.";
}

function roleEdgeConflictInfoText() {
  return "The declared role label and the authorised edge structure diverge. PAT follows the confirmed DAG structure while flagging the role mismatch for review.";
}

function multipleRoutesInfoText() {
  return "The confirmed graph yields more than one intervention query. Interpret each route separately; do not merge pathway, direct, and confounder-adjustment logic.";
}

function addPdfText(doc, text, options) {
  const {
    x = 14,
    y,
    maxWidth = 180,
    lineHeight = 6,
    pageHeight,
    bottomMargin = 14,
    fontSize = 10,
    fontStyle = "normal",
  } = options;

  let cursorY = y;

  doc.setFont("helvetica", fontStyle);
  doc.setFontSize(fontSize);

  const lines = pdfValuePreserveLines(text)
    .split("\n")
    .flatMap((line) => doc.splitTextToSize(line, maxWidth));

  lines.forEach((line) => {
    if (cursorY + lineHeight > pageHeight - bottomMargin) {
      doc.addPage();
      cursorY = 14;
    }

    doc.text(line, x, cursorY);
    cursorY += lineHeight;
  });

  return cursorY;
}

function addPdfFooters(doc, footerText) {
  const pageCount = doc.internal.getNumberOfPages();
  const pageWidth = doc.internal.pageSize.width;
  const pageHeight = doc.internal.pageSize.height;

  for (let page = 1; page <= pageCount; page += 1) {
    doc.setPage(page);
    doc.setFont("helvetica", "normal");
    doc.setFontSize(8);

    doc.text(pdfValuePreserveLines(footerText), 14, pageHeight - 8);
    doc.text(`Page ${page} of ${pageCount}`, pageWidth - 38, pageHeight - 8);
  }
}

function addPdfSection(doc, title, body, options) {
  const pageHeight = doc.internal.pageSize.height;
  let y = options.y;

  if (y + 14 > pageHeight - 14) {
    doc.addPage();
    y = 14;
  }

  y = addPdfText(doc, title, {
    y,
    pageHeight,
    fontSize: 12,
    fontStyle: "bold",
  });

  y += 2;

  if (Array.isArray(body)) {
    body.forEach((item) => {
      y = addPdfText(doc, item, {
        y,
        pageHeight,
        fontSize: 10,
      });
      y += 2;
    });
  } else {
    y = addPdfText(doc, body, {
      y,
      pageHeight,
      fontSize: 10,
    });
  }

  return y + 4;
}

function downloadPostDagReportAsPDF(report) {
  if (!report || typeof report !== "object") {
    throw new Error(
      "downloadPostDagReportAsPDF received no valid report object.",
    );
  }

  const doc = new jsPDF();
  const pageHeight = doc.internal.pageSize.height;
  let y = 14;

  y = addPdfText(doc, "PAT-GPT Post-DAG Causal Audit Report", {
    y,
    pageHeight,
    fontSize: 15,
    fontStyle: "bold",
  });

  y += 2;

  y = addPdfText(doc, `Generated: ${report.generatedAt}`, {
    y,
    pageHeight,
    fontSize: 9,
  });

  y += 6;

  y = addPdfSection(doc, "1. User input", report.rawInputText, { y });

  y = addPdfSection(
    doc,
    "2. Interactive pre-DAG audit trail",
    report.preDagAuditTrail?.length
      ? report.preDagAuditTrail
      : [
          "No pre-DAG audit trail was available.",
          "Post-DAG analysis proceeds from the confirmed user-authorised graph.",
        ],
    { y },
  );

  y = addPdfSection(
    doc,
    "3. Confirmed causal model summary",
    [
      `Initial causal-form candidate: ${report.audit.apparentCause}`,
      `Confirmed outcome: ${report.audit.confirmedOutcome}`,
      report.audit.graphSummaryText,
      report.audit.comparisonSummaryText,
      report.audit.confirmedDagRelations.length
        ? `Confirmed DAG relations: ${report.audit.confirmedDagRelations.join("; ")}`
        : "Confirmed DAG relations: —",
      report.unconnectedRoleAudit?.hasUnconnected
        ? report.unconnectedRoleAudit.summary
        : null,
    ].filter(Boolean),
    { y },
  );

  y = addPdfSection(
    doc,
    "4. Intervention identifiability (do-calculus)",
    [
      `Status: ${report.intervention.badge}`,
      report.intervention.body,
      ...report.intervention.estimands.flatMap((item) => {
        const support = item.supportSet
          ? ` ${item.supportSet.label}: ${item.supportSet.value}.`
          : "";

        const conflictLines = safeArray(item.conflictPaths).map((path) => {
          const conflictLabels =
            safeArray(path.conflictLabels).length > 0
              ? ` Role conflict variable${
                  path.conflictLabels.length === 1 ? "" : "s"
                }: ${path.conflictLabels.join(", ")}.`
              : "";

          return `Conflict pathway: ${path.pathText}.${conflictLabels}`;
        });
        const directLines = safeArray(item.directRelations).map(
          (relation) => `Authorised direct relation: ${relation.relationText}.`,
        );

        const mediatedHeading =
          item.method === "frontdoor"
            ? "Authorised mediated pathway"
            : "Additional authorised pathway structure";

        const mediatedLines = safeArray(item.mediatedPaths).flatMap((path) => {
          const lines = [`${mediatedHeading}: ${path.pathText}.`];

          if (item.method !== "frontdoor") {
            lines.push(
              "This pathway is present in the confirmed DAG, but it is not being treated as a front-door identification result.",
            );
          }

          return lines;
        });

        const dSeparationLines = item.dSeparationAudit?.summary
          ? [`D-separation audit: ${item.dSeparationAudit.summary}`]
          : [];

        const cautionLines = safeArray(item.cautionStructures).flatMap(
          (structure) => {
            const relationLines = safeArray(structure.relations).map(
              (relation) => `Implicated DAG structure: ${relation}.`,
            );

            const colliderFormLine =
              typeof structure.colliderForm === "string" &&
              structure.colliderForm.trim()
                ? `Collider form: ${structure.colliderForm}.`
                : null;

            const colliderLine =
              safeArray(structure.colliderLabels).length > 0
                ? `Collider-risk variable${
                    structure.colliderLabels.length === 1 ? "" : "s"
                  }: ${structure.colliderLabels.join(", ")}.`
                : null;

            return [...relationLines, colliderFormLine, colliderLine].filter(
              Boolean,
            );
          },
        );

        return [
          `${item.label}: ${item.body}${support}`,
          ...dSeparationLines,
          ...directLines,
          ...mediatedLines,
          ...conflictLines,
          ...cautionLines,
        ];
      }),
      report.intervention.note,
    ],
    { y },
  );

  y = addPdfSection(
    doc,
    "5. Bayesian Causal Stability Metric (BCSM)",
    [
      `BCSM: ${report.bcsm.percent}`,
      `Band: ${report.bcsm.bandLabel}`,
      report.bcsm.bandDescription,
      report.bcsm.roleAwareNarrative,
      `Calibration note: ${report.bcsm.calibrationNote}`,
    ],
    { y },
  );

  y = addPdfSection(
    doc,
    "6. Intervention scope and model limits",
    report.actionLegitimacyText,
    {
      y,
    },
  );

  y = addPdfSection(
    doc,
    "7. Claim-form and structural audit",
    [
      report.audit.semanticSummaryText,
      report.audit.semanticSubclaims.length
        ? `Initial semantic subclaims: ${report.audit.semanticSubclaims.join("; ")}`
        : "Initial semantic subclaims: —",
      report.audit.evidenceSummaryText,
      report.audit.retainedFromWording.length
        ? `Retained from wording: ${report.audit.retainedFromWording.join("; ")}`
        : report.audit.confirmedDagRelations.length
          ? "Retained from wording: See confirmed DAG relations."
          : "Retained from wording: —",
      report.audit.notRetainedFromWording.length
        ? `Not retained or not directly represented in the DAG: ${report.audit.notRetainedFromWording.join("; ")}`
        : `Not retained or not directly represented in the DAG: ${defaultNotRepresentedByDagItems().join("; ")}`,
      report.audit.evidenceItems.length
        ? `Sentence-level input support: ${report.audit.evidenceItems
            .map((item) => {
              const linkText = item.links.length
                ? ` linked to ${item.links.join(", ")}`
                : "";
              return `${item.id} [${item.role}; type: ${item.type}; strength: ${item.strength}${linkText}] ${item.text}`;
            })
            .join(" | ")}`
        : "Sentence-level input support: —",
      report.audit.evidenceFooterNote,
      report.audit.auditBoundaryWarning ||
        report.audit.auditNotes ||
        report.audit.auditSummary ||
        "This audit is structural and linguistic, not empirical evidence.",
    ],
    { y },
  );

  y = addPdfSection(
    doc,
    "8. User input causal form and assertion strength",
    [
      `Input wording: ${report.bayes.statement}`,
      `Starting assertion strength: ${report.bayes.prior}`,
      `Adjusted assertion strength: ${report.bayes.posterior}`,
      `Assessment label: ${report.bayes.label}`,
      report.bayes.explanation,
      report.bayes.notes,
    ],
    { y },
  );

  y = addPdfSection(
    doc,
    "9. Illustrative pathways",
    [
      "Epistemic expansion beyond the bounded DAG.",
      "This section references the confirmed DAG as the formal causal structure; however, the DAG does not exhaust the inquiry. The pathways below support conceptual reframing, measurement critique, evidence integration, and model-boundary recognition.",
      pdfIllustrativePathwayStatus(report.intervention),
      pdfIllustrativePathwayContext(report.intervention),
      ...(report.pathways.length
        ? report.pathways.flatMap((pathway, index) => {
            const label = pdfPathwayHeading(pathway);
            const parts = pdfPathwayParts(pathway);

            return [
              `${index + 1}. ${label}`,
              `Description: ${parts.description}`,
              parts.workedExample
                ? `Worked example: ${parts.workedExample}`
                : null,
            ].filter(Boolean);
          })
        : ["No illustrative pathways were available."]),
    ],
    { y },
  );

  y = addPdfSection(
    doc,
    "10. Boundary note",
    [
      "This report records PAT-GPT's confirmed post-DAG causal audit state.",
      "PAT interprets the confirmed DAG, not alternative semantic readings of the original prompt. Alternative causal roles require graph revision and confirmation.",
      "It assesses causal structure, intervention identifiability, linguistic assertion strength, and causal-legitimacy posture.",
      "It does not establish empirical truth, effect magnitude, real-world feasibility, or policy sufficiency.",
    ],
    { y },
  );

  addPdfFooters(
    doc,
    report.footerText ||
      "PAT v3.6-20260428 | UI v5.0-20260430 | Post-DAG report",
  );

  const safeRunId =
    typeof report.pipelineRunId === "string" && report.pipelineRunId.trim()
      ? report.pipelineRunId.trim().slice(0, 8)
      : String(Date.now());

  const filename = `pat_post_dag_report_${safeRunId}.pdf`;

  const blob = doc.output("blob");
  const url = URL.createObjectURL(blob);

  const link = document.createElement("a");
  link.href = url;
  link.download = filename;
  link.rel = "noopener";
  link.style.display = "none";

  document.body.appendChild(link);

  window.setTimeout(() => {
    link.click();
    console.log("[PAT PDF] download click dispatched:", filename);
  }, 0);

  window.setTimeout(() => {
    link.remove();
    URL.revokeObjectURL(url);
    console.log("[PAT PDF] download resources released:", filename);
  }, 10000);

  console.log("[PAT PDF] download prepared:", filename);
}

function pdfPathwayHeading(pathway) {
  switch (pathway?.epistemic_domain) {
    case "recontextualisation":
      return "Conceptual framing";
    case "recalibration":
      return "Measurement and operationalisation";
    case "reintegration":
      return "Evidence integration";
    case "boundary_recognition":
      return "Model boundary and scope";
    default:
      return pathway?.title || pathway?.heading || "Illustrative pathway";
  }
}

function pdfHasMultipleIdentificationRoutes(intervention) {
  if (
    Array.isArray(intervention?.estimands) &&
    intervention.estimands.length > 1
  ) {
    return true;
  }

  const badge =
    typeof intervention?.badge === "string" ? intervention.badge : "";

  return badge.toLowerCase().trim() === "multiple identification routes";
}

function pdfIllustrativePathwayStatus(intervention) {
  if (pdfHasMultipleIdentificationRoutes(intervention)) {
    return "Multiple identification routes remain active.";
  }

  const m =
    typeof intervention?.method === "string"
      ? intervention.method.toLowerCase().trim()
      : "";

  if (m === "collider_caution") return "Collider caution remains active.";
  if (m === "role_edge_conflict") return "Role-edge conflict remains active.";
  if (m === "frontdoor")
    return "Mediated/pathway structure remains structural.";
  if (m === "identifiable_under_adjustment") {
    return "Adjustment interpretation remains conditional.";
  }
  if (m === "direct") return "Direct structure remains structural.";
  if (m === "unidentifiable") return "Unidentified structure remains active.";

  return "Do-calculus boundary remains active.";
}

function pdfIllustrativePathwayContext(intervention) {
  if (pdfHasMultipleIdentificationRoutes(intervention)) {
    return "The do-calculus layer identified more than one structural route. The illustrative pathways do not merge these routes or resolve their differences; they support conceptual, measurement, evidentiary, and boundary review across the confirmed model.";
  }

  const m =
    typeof intervention?.method === "string"
      ? intervention.method.toLowerCase().trim()
      : "";

  if (m === "collider_caution") {
    return "The collider / adjustment caution identified in the do-calculus layer remains in force. The illustrative pathways do not authorise adjustment, intervention, or causal estimation; they help inspect what the current structure may compress, omit, or operationalise too narrowly.";
  }

  if (m === "role_edge_conflict") {
    return "The role-edge conflict identified in the do-calculus layer remains in force. The illustrative pathways do not resolve the mismatch; they support review of whether the graph, role assignment, or modelling frame should be revised.";
  }

  if (m === "frontdoor") {
    return "The mediated/pathway structure remains a structural graph result. The illustrative pathways extend that structure into conceptual, measurement, evidentiary, and boundary questions without converting it into a Pearl front-door identification result, empirical proof of mediation, effect magnitude, or real-world feasibility.";
  }

  if (m === "identifiable_under_adjustment") {
    return "The adjustment-based interpretation remains conditional on the confirmed DAG and modelling assumptions. The illustrative pathways broaden review of how those assumptions, measurements, and evidentiary boundaries might be questioned or refined.";
  }

  if (m === "direct") {
    return "The direct-identification result remains a structural graph result. The illustrative pathways broaden review of how the direct relation is framed, measured, evidenced, and bounded.";
  }

  if (m === "unidentifiable") {
    return "The current graph does not support a uniquely identified intervention effect. The illustrative pathways remain valuable because they help inspect conditions that may guide conceptual clarification, measurement refinement, or graph revision.";
  }

  return "The illustrative pathways preserve the do-calculus result while opening conceptual, methodological, evidentiary, and boundary questions for further review.";
}

function pdfPathwayParts(pathway) {
  const description =
    typeof pathway?.description === "string" && pathway.description.trim()
      ? pathway.description.trim()
      : null;

  const workedExample =
    typeof pathway?.worked_example === "string" && pathway.worked_example.trim()
      ? pathway.worked_example.trim()
      : null;

  const narrative =
    typeof pathway?.narrative === "string" && pathway.narrative.trim()
      ? pathway.narrative.trim()
      : null;

  if (description || workedExample) {
    return {
      description: description || "No pathway description available.",
      workedExample: workedExample || null,
    };
  }

  return {
    description: narrative || "No pathway narrative available.",
    workedExample: null,
  };
}

function deriveBayesExplanation(prior, posterior) {
  if (typeof prior !== "number" || typeof posterior !== "number") return null;

  if (posterior >= 0.65) {
    return "Strengthened. The wording leans more clearly toward a causal claim.";
  }

  if (posterior >= 0.55) {
    return "Slightly strengthened. The wording leans toward a causal claim, but only mildly.";
  }

  if (posterior > 0.45) {
    return "Neutral. The wording does not materially strengthen or weaken the causal-form claim.";
  }

  if (posterior >= 0.36) {
    return "Slightly weakened. The wording introduces some caution or scepticism about the causal claim.";
  }

  return "Weakened. The wording noticeably reduces the causal wording strength of the sentence.";
}
function bayesCredenceClasses(value) {
  if (typeof value !== "number" || Number.isNaN(value)) {
    return "bg-slate-800 border-slate-600 text-slate-300";
  }

  if (value >= 0.65) {
    return "bg-emerald-900/40 border-emerald-700/80 text-emerald-200";
  }

  if (value >= 0.55) {
    return "bg-sky-900/40 border-sky-700/80 text-sky-200";
  }

  if (value > 0.45) {
    return "bg-slate-800 border-slate-600 text-slate-200";
  }

  if (value >= 0.36) {
    return "bg-amber-900/40 border-amber-700/80 text-amber-200";
  }

  return "bg-rose-900/40 border-rose-700/80 text-rose-200";
}

function bayesCredenceLabel(value) {
  if (typeof value !== "number" || Number.isNaN(value)) return "Unavailable";

  if (value >= 0.65) return "Strengthened";
  if (value >= 0.55) return "Slightly strengthened";
  if (value > 0.45) return "Neutral";
  if (value >= 0.36) return "Slightly weakened";
  return "Weakened";
}

function safeArray(value) {
  return Array.isArray(value) ? value : [];
}

function safeObject(value) {
  return value && typeof value === "object" ? value : {};
}

function relationPlainText(text) {
  if (typeof text !== "string" || !text.trim()) return "—";
  return text.replace(/\s*→\s*/g, " leads to ");
}

function formatRelationList(relations) {
  return relations
    .map((rel) => rel?.relation_text)
    .filter((text) => typeof text === "string" && text.trim())
    .map((text) => relationPlainText(text));
}

function formatSemanticSubclaims(subclaims) {
  return safeArray(subclaims).filter(
    (text) => typeof text === "string" && text.trim(),
  );
}

function formatDisplayValue(value) {
  if (Array.isArray(value)) {
    const cleaned = value.filter(
      (item) => typeof item === "string" && item.trim(),
    );
    return cleaned.length ? cleaned.join(", ") : "—";
  }

  if (typeof value === "string" && value.trim()) {
    return value.trim();
  }

  return "—";
}

function formatCausalEntryLanguage(entryAudit) {
  const terms = safeArray(entryAudit?.causal_language).filter(
    (item) => typeof item === "string" && item.trim(),
  );

  return terms;
}

function formatCausalEntryTrigger(entryAudit) {
  const trigger =
    typeof entryAudit?.causal_entry_trigger === "string"
      ? entryAudit.causal_entry_trigger.trim()
      : "";

  switch (trigger) {
    case "explicit_causal_operator":
      return "explicit causal operator";
    case "intervention_choice_structure":
      return "intervention-choice structure";
    case "counterfactual_condition":
      return "counterfactual condition";
    case "association_requiring_causal_refinement":
      return "association requiring causal refinement";
    default:
      return "";
  }
}

function cleanAuditDisplayText(value) {
  const text = formatDisplayValue(value);
  if (text === "—") return text;

  return text
    .replace(/^do\s+/i, "")
    .replace(/\s+/g, " ")
    .trim();
}

function normaliseForComparison(text) {
  if (typeof text !== "string") return "";
  return text
    .toLowerCase()
    .replace(/\s+/g, " ")
    .replace(/\bleads to\b/g, "causes")
    .replace(/\s*→\s*/g, " causes ")
    .trim();
}

function arraysBroadlyAlign(left, right) {
  const a = safeArray(left).map(normaliseForComparison).filter(Boolean);
  const b = safeArray(right).map(normaliseForComparison).filter(Boolean);

  if (!a.length || !b.length) return false;
  if (a.length !== b.length) return false;

  return a.every((item, idx) => item === b[idx]);
}

function describeGraphSummary(relations, outcomeLabel) {
  const count = safeArray(relations).length;
  if (!count && !outcomeLabel) return "No confirmed graph structure available.";

  if (count && outcomeLabel && outcomeLabel !== "—") {
    return `You confirmed ${count} directed relation${count === 1 ? "" : "s"} in the DAG, with ${outcomeLabel} as the outcome.`;
  }

  if (count) {
    return `You confirmed ${count} directed relation${count === 1 ? "" : "s"} in the DAG.`;
  }

  return `You confirmed a DAG with ${outcomeLabel} as the outcome.`;
}
function confirmedOutcomeLabelFromGraph(graph, fallback = "—") {
  const yNode = safeArray(graph?.nodes).find((node) => node?.role === "Y");

  if (typeof yNode?.label === "string" && yNode.label.trim()) {
    return yNode.label.trim();
  }

  if (typeof yNode?.displayLabel === "string" && yNode.displayLabel.trim()) {
    return yNode.displayLabel.trim();
  }

  return fallback;
}

function formatRoleAssignmentForPdf(variables) {
  if (!variables || typeof variables !== "object") return [];

  const rows = [];

  safeArray(variables.predictors).forEach((item, index) => {
    const label = item?.label || item?.displayLabel || item;
    if (typeof label === "string" && label.trim()) {
      rows.push(`X${index + 1} = ${label.trim()}`);
    }
  });

  safeArray(variables.mediators).forEach((item, index) => {
    const label = item?.label || item?.displayLabel || item;
    if (typeof label === "string" && label.trim()) {
      rows.push(`M${index + 1} = ${label.trim()}`);
    }
  });

  safeArray(variables.confounders).forEach((item, index) => {
    const label = item?.label || item?.displayLabel || item;
    if (typeof label === "string" && label.trim()) {
      rows.push(`Z${index + 1} = ${label.trim()}`);
    }
  });

  const outcomeLabel =
    variables.outcome?.label ||
    variables.outcome?.displayLabel ||
    variables.outcome;

  if (typeof outcomeLabel === "string" && outcomeLabel.trim()) {
    rows.push(`Y = ${outcomeLabel.trim()}`);
  }

  return rows;
}

function articleForWord(value) {
  const text = typeof value === "string" ? value.trim().toLowerCase() : "";
  return /^[aeiou]/.test(text) ? "an" : "a";
}

function describeInitialReadingForPdf(originalSemantic, originalClaimFrame) {
  const semantic = safeObject(originalSemantic);
  const claim = safeObject(originalClaimFrame);

  const modalityRaw =
    semantic.modality ||
    claim.modality ||
    semantic.form ||
    claim.surface_form ||
    claim.frame_type ||
    "unspecified";

  const modality = String(modalityRaw).toLowerCase();
  const article = articleForWord(modality);

  const x = formatDisplayValue(semantic.X || claim.X);
  const y = formatDisplayValue(semantic.Y || claim.Y);

  if (x !== "—" && y !== "—") {
    return `PAT initially read the input as ${article} ${modality} causal-form inquiry linking ${x} to ${y}.`;
  }

  return `PAT initially read the input as ${article} ${modality} causal-form inquiry; however, the initial semantic frame did not fully settle a single X/Y structure.`;
}

function describeFormationModeForPdf(claimFrame, confirmedClaimFrame) {
  const frame = safeObject(claimFrame);
  const confirmed = safeObject(confirmedClaimFrame);

  const frameType =
    frame.frame_type || confirmed.frame_type || frame.surface_form || "";

  const modality = frame.modality || confirmed.modality || "";

  const resolvedMode =
    frame.resolved_variables?.mode || confirmed.resolved_variables?.mode || "";

  if (
    frameType === "distillation_partition" ||
    confirmed.frame_type === "distillation_partition" ||
    resolvedMode === "distillation_partition" ||
    modality === "authorial" ||
    confirmed.modality === "authorial"
  ) {
    return "Pre-DAG formation step: the final role set reflects an authorial or distillation partition. This means the confirmed role assignment was settled through user-authorised model formation rather than automatic extraction alone.";
  }

  if (
    frame.frame_type === "Interrogative" ||
    confirmed.frame_type === "Interrogative"
  ) {
    return "Pre-DAG formation step: the input was treated as a causal-form question. The confirmed DAG records the user-authorised structure used for post-DAG analysis.";
  }

  return "Pre-DAG formation step: the user reviewed the causal roles and authorised the DAG structure before post-DAG analysis proceeded.";
}

function buildPreDagAuditTrail({
  rawInputText,
  originalSemantic,
  originalClaimFrame,
  claimFrame,
  confirmedClaimFrame,
  variables,
  confirmedDagRelations,
  unconnectedRoleAudit,
  pipelineRunId,
}) {
  const roleRows = formatRoleAssignmentForPdf(variables);
  const relationRows = safeArray(confirmedDagRelations).filter(
    (item) => typeof item === "string" && item.trim(),
  );

  const unconnectedRows = safeArray(unconnectedRoleAudit?.variables)
    .map((item) => {
      const label = item?.label || "";
      const role = item?.role ? formatRoleLabel(item.role) : "variable";
      return label ? `${label} (${role})` : null;
    })
    .filter(Boolean);

  return [
    "This section records how the causal structure was formed before DAG confirmation. It documents PAT's initial interpretation of the input, the user-confirmed role assignment, and the final structure submitted for post-DAG analysis. This is an audit trail of model formation, not empirical evidence for the confirmed causal structure.",
    `PAT's initial reading: ${describeInitialReadingForPdf(
      originalSemantic,
      originalClaimFrame,
    )}`,
    describeFormationModeForPdf(claimFrame, confirmedClaimFrame),
    roleRows.length
      ? ["User-confirmed role key:", ...roleRows].join("\n")
      : "User-confirmed role key: —",
    relationRows.length
      ? ["Confirmed DAG relations:", ...relationRows].join("\n")
      : "Confirmed DAG relations: —",
    unconnectedRows.length
      ? [
          "Role-authorised but not edge-connected:",
          ...unconnectedRows,
          "These variables were included in the confirmed role settlement; however, no directed edges involving them were included in the confirmed DAG.",
        ].join("\n")
      : null,
    pipelineRunId ? `Pipeline run ID: ${pipelineRunId}` : null,
    "Formation note: post-DAG analysis proceeds from the confirmed user-authorised graph, not from the natural-language wording alone. The confirmed DAG does not establish empirical truth, effect size, or real-world intervention validity.",
  ].filter(Boolean);
}

function defaultNotRepresentedByDagItems() {
  return [
    "Effect magnitude or degree of mediation is not represented by the DAG.",
    "Statistical estimation, empirical support, and real-world feasibility are not represented by the DAG.",
    "The DAG records authorised structural relations; it does not establish empirical truth or intervention success.",
  ];
}

function normaliseUnconnectedRoleAudit(src, pipelineResult) {
  const audit =
    src?.unconnected_role_variable_audit ||
    pipelineResult?.unconnected_role_variable_audit ||
    pipelineResult?.pipeline_result?.unconnected_role_variable_audit ||
    null;

  if (!audit || typeof audit !== "object") {
    return {
      status: "ok",
      variables: [],
      summary: "",
      hasUnconnected: false,
    };
  }

  const variables = safeArray(audit.variables).filter(
    (item) =>
      item &&
      typeof item === "object" &&
      typeof item.label === "string" &&
      item.label.trim(),
  );

  return {
    status: audit.status || "ok",
    variables,
    summary:
      typeof audit.summary === "string" && audit.summary.trim()
        ? audit.summary.trim()
        : "",
    hasUnconnected: variables.length > 0,
  };
}

function unconnectedRoleAuditInfoText() {
  return "These variables were present in the confirmed X/M/Z/Y role settlement; however, no directed edges involving them were included in the confirmed DAG. PAT accounts for them as nominated variables, yet does not treat them as structurally active in the edge-confirmed graph.";
}

function formatRoleLabel(role) {
  const r = typeof role === "string" ? role.toLowerCase().trim() : "";

  if (r === "predictor") return "predictor";
  if (r === "mediator") return "mediator";
  if (r === "confounder") return "confounder";
  if (r === "outcome") return "outcome";

  return r || "variable";
}

function buildPdfEvidenceLinkMap(evidenceLinks) {
  const map = new Map();
  const rows = safeArray(evidenceLinks?.links);

  rows.forEach((row, idx) => {
    const subclaimLabel =
      typeof row?.subclaim_index === "number"
        ? `S${row.subclaim_index + 1}`
        : `S${idx + 1}`;

    [
      ...safeArray(row?.supporting_evidence_ids),
      ...safeArray(row?.counter_evidence_ids),
      ...safeArray(row?.neutral_evidence_ids),
    ].forEach((id) => {
      if (typeof id !== "string" || !id.trim()) return;

      if (!map.has(id)) {
        map.set(id, new Set());
      }

      map.get(id).add(subclaimLabel);
    });
  });

  return map;
}

function buildPdfEvidenceItems(evidence, evidenceLinks, audit) {
  const items = safeArray(evidence?.evidence_items);
  const directLinkMap = buildPdfEvidenceLinkMap(evidenceLinks);
  const unlinkedIds = new Set(safeArray(evidenceLinks?.unlinked_evidence_ids));

  const auditItems = safeArray(audit?.evidence_items);
  const auditItemById = new Map();

  auditItems.forEach((item) => {
    if (typeof item?.id === "string" && item.id.trim()) {
      auditItemById.set(item.id, item);
    }
  });

  return items.map((item, idx) => {
    const id = item?.id || `E${idx + 1}`;
    const auditRow = auditItemById.get(id) || null;

    const linkedSubclaims = Array.from(directLinkMap.get(id) || []);
    const fallbackLinkedSubclaims = safeArray(auditRow?.linked_subclaims);

    const links =
      linkedSubclaims.length > 0
        ? linkedSubclaims
        : fallbackLinkedSubclaims.length > 0
          ? fallbackLinkedSubclaims
          : unlinkedIds.has(id)
            ? ["unlinked"]
            : [];

    return {
      id,
      role: item?.role || "neutral",
      type: item?.type || "unspecified",
      strength: item?.strength_label || "unspecified",
      text: item?.text || "—",
      links,
    };
  });
}

function deriveDirectEvidenceOverview(evidence, evidenceLinks) {
  const evidenceItems = safeArray(evidence?.evidence_items);
  const links = safeArray(evidenceLinks?.links);
  const unlinkedEvidenceIds = safeArray(evidenceLinks?.unlinked_evidence_ids);

  const linkedEvidenceIds = new Set();

  for (const link of links) {
    for (const id of safeArray(link?.supporting_evidence_ids)) {
      if (typeof id === "string" && id.trim()) linkedEvidenceIds.add(id);
    }
    for (const id of safeArray(link?.counter_evidence_ids)) {
      if (typeof id === "string" && id.trim()) linkedEvidenceIds.add(id);
    }
    for (const id of safeArray(link?.neutral_evidence_ids)) {
      if (typeof id === "string" && id.trim()) linkedEvidenceIds.add(id);
    }
  }

  const linkedSubclaimCount = links.filter((link) => {
    return (
      safeArray(link?.supporting_evidence_ids).length > 0 ||
      safeArray(link?.counter_evidence_ids).length > 0 ||
      safeArray(link?.neutral_evidence_ids).length > 0
    );
  }).length;

  return {
    total_items: evidenceItems.length,
    linked_items: linkedEvidenceIds.size,
    unlinked_items: unlinkedEvidenceIds.length,
    linked_subclaims: linkedSubclaimCount,
    has_payload:
      evidenceItems.length > 0 ||
      links.length > 0 ||
      unlinkedEvidenceIds.length > 0,
  };
}

function normaliseDoResults(patDo) {
  if (!patDo || typeof patDo !== "object") return [];

  if (
    typeof patDo.method === "string" ||
    typeof patDo.identifiable === "boolean" ||
    typeof patDo.intervention === "string"
  ) {
    return [patDo];
  }

  return Object.values(patDo).filter(
    (item) =>
      item &&
      typeof item === "object" &&
      (typeof item.method === "string" ||
        typeof item.identifiable === "boolean" ||
        typeof item.intervention === "string"),
  );
}

function methodBadgeLabel(method) {
  const m = typeof method === "string" ? method.toLowerCase().trim() : "";

  if (m === "direct") return "Directly identifiable";
  if (m === "identifiable_under_adjustment")
    return "Identifiable under adjustment";
  if (m === "frontdoor") return "Mediated/pathway structure identified";
  if (m === "collider_caution") return "Collider / adjustment caution";
  if (m === "role_edge_conflict") return "Role-edge conflict";
  if (m === "identifiable") return "Identifiable";
  if (m === "no-graph") return "No graph available";
  return "Unidentifiable";
}

function panelBadgeLabel(method, estimandCount) {
  if (estimandCount > 1) {
    return "Multiple identification routes";
  }

  return methodBadgeLabel(method);
}

function methodBadgeClasses(method) {
  const m = typeof method === "string" ? method.toLowerCase().trim() : "";

  if (
    m === "direct" ||
    m === "identifiable" ||
    m === "identifiable_under_adjustment" ||
    m === "frontdoor"
  ) {
    return "bg-emerald-900/40 border-emerald-700/80 text-emerald-200";
  }

  if (m === "collider_caution" || m === "role_edge_conflict") {
    return "bg-amber-900/40 border-amber-700/80 text-amber-200";
  }

  if (m === "no-graph") {
    return "bg-slate-800 border-slate-600 text-slate-300";
  }

  return "bg-rose-900/40 border-rose-700/80 text-rose-200";
}

function panelBadgeClasses(method, estimandCount) {
  if (estimandCount > 1) {
    return "bg-sky-900/40 border-sky-700/80 text-sky-200";
  }

  return methodBadgeClasses(method);
}

function formatDoEstimand(doItem) {
  const x = doItem?.X_display || doItem?.X || doItem?.intervention || "X";
  const y = doItem?.Y_display || doItem?.Y || "Y";

  const cleanX =
    typeof x === "string" ? x.replace(/^do\((.*)\)$/i, "$1").trim() : "X";
  const cleanY = typeof y === "string" ? y.trim() : "Y";

  return `do(${cleanX}) → ${cleanY}`;
}

function hasDirectXYEdge(graph, doItem) {
  const xId = doItem?.X || doItem?.intervention || "";
  const yId = doItem?.Y || "";

  if (!xId || !yId) return false;

  return safeArray(graph?.edges).some((edge) => {
    const from = edge?.from ?? edge?.source ?? "";
    const to = edge?.to ?? edge?.target ?? "";

    return from === xId && to === yId;
  });
}

function inferDisplayDoMethod(doItem, nodeLookup) {
  const method =
    typeof doItem?.method === "string"
      ? doItem.method.toLowerCase().trim()
      : "";

  if (method === "frontdoor") {
    return "frontdoor";
  }

  return method;
}

function describeDoMethod(doItem, nodeLookup, graph) {
  const method = inferDisplayDoMethod(doItem, nodeLookup);
  const x = doItem?.X_display || doItem?.X || "X";
  const y = doItem?.Y_display || doItem?.Y || "Y";

  const rawSet = safeArray(doItem?.adjustment_set).filter(
    (item) => typeof item === "string" && item.trim(),
  );

  const confounders = formatRoleAwareSet(rawSet, nodeLookup, ["Z"]);
  const mediators = formatRoleAwareSet(rawSet, nodeLookup, ["M"]);

  if (method === "direct") {
    return `The confirmed DAG supports direct structural identification of the intervention effect from ${x} to ${y}. No adjustment set is required by this graph.`;
  }

  if (method === "identifiable_under_adjustment") {
    const adjustmentText = confounders.length
      ? confounders.map((item) => item.label).join(", ")
      : rawSet.join(", ") || "the specified variables";

    return `The confirmed DAG supports structural identification of the intervention effect from ${x} to ${y}, conditional on candidate confounder adjustment for ${adjustmentText}.`;
  }

  if (method === "frontdoor") {
    const pathwayText = mediators.length
      ? mediators.map((item) => item.label).join(", ")
      : rawSet.join(", ") || "the specified pathway";

    const directXY = hasDirectXYEdge(graph, doItem);

    if (directXY) {
      return `The confirmed DAG contains both a direct ${x} → ${y} relation and authorised mediated/pathway structure through ${pathwayText}. These pathway variables should not be described as default confounder-adjustment variables for the total effect. This is not being treated as a Pearl front-door identification result.`;
    }

    return `The confirmed DAG contains authorised mediated/pathway structure from ${x} to ${y} through ${pathwayText}. These pathway variables should not be described as default confounder-adjustment variables for the total effect. This is not being treated as a Pearl front-door identification result.`;
  }

  if (method === "identifiable") {
    return `The confirmed DAG supports structural identification of the intervention effect from ${x} to ${y}.`;
  }

  if (method === "collider_caution") {
    return `The confirmed DAG contains a collider or collider-risk structure relevant to the ${x} to ${y} query. Adjustment should not proceed without checking whether conditioning would open rather than block a path.`;
  }

  if (method === "role_edge_conflict") {
    return (
      doItem?.notes ||
      `The confirmed DAG contains a role-edge conflict for the ${x} to ${y} query. A declared role label does not match the authorised edge structure.`
    );
  }

  if (method === "no-graph") {
    return "No confirmed causal graph was available for do-calculus assessment.";
  }

  return `The confirmed DAG does not support a uniquely identified intervention effect from ${x} to ${y}.`;
}

function buildNodeLookup(graph, causalModel) {
  const lookup = new Map();

  const addNode = (node) => {
    if (!node || typeof node !== "object") return;
    if (typeof node.id !== "string" || !node.id.trim()) return;

    lookup.set(node.id.trim(), {
      id: node.id.trim(),
      label: node.displayLabel || node.label || node.id.trim(),
      role: typeof node.role === "string" ? node.role.trim().toUpperCase() : "",
    });
  };

  safeArray(graph?.nodes).forEach(addNode);
  safeArray(causalModel?.nodes).forEach(addNode);

  return lookup;
}

function formatRoleAwareSet(ids, nodeLookup, allowedRoles = null) {
  return safeArray(ids)
    .map((id) => {
      if (typeof id !== "string" || !id.trim()) return null;
      const cleanId = id.trim();
      const node = nodeLookup.get(cleanId);

      if (!node) {
        return {
          id: cleanId,
          label: cleanId,
          role: "",
        };
      }

      return node;
    })
    .filter((node) => {
      if (!node) return false;
      if (!allowedRoles) return true;
      return allowedRoles.includes(node.role);
    });
}

function describeDoSupportSet(doItem, nodeLookup, graph) {
  const method = inferDisplayDoMethod(doItem, nodeLookup);

  const rawSet = safeArray(doItem?.adjustment_set).filter(
    (item) => typeof item === "string" && item.trim(),
  );

  if (!rawSet.length) return null;

  if (method === "frontdoor") {
    const mediators = formatRoleAwareSet(rawSet, nodeLookup, ["M"]);
    return {
      label: "Pathway variables",
      value: mediators.length
        ? mediators.map((item) => item.label).join(", ")
        : rawSet.join(", "),
      note: "These are mediator/pathway variables, not default confounder-adjustment variables for the total effect.",
    };
  }

  if (method === "identifiable_under_adjustment") {
    const confounders = formatRoleAwareSet(rawSet, nodeLookup, ["Z"]);
    const displayItems = confounders.length
      ? confounders
      : formatRoleAwareSet(rawSet, nodeLookup);

    return {
      label: "Candidate confounder adjustment set",
      value: displayItems.map((item) => item.label).join(", "),
    };
  }

  const supportItems = formatRoleAwareSet(rawSet, nodeLookup);

  return {
    label: "Supporting set",
    value: supportItems.length
      ? supportItems.map((item) => item.label).join(", ")
      : rawSet.join(", "),
  };
}

function formatDoConflictPaths(doItem) {
  return safeArray(doItem?.conflict_paths)
    .map((path) => {
      const pathText =
        typeof path?.path_text === "string" && path.path_text.trim()
          ? path.path_text.trim()
          : "";

      if (!pathText) return null;

      const conflictLabels = safeArray(path?.conflict_labels).filter(
        (item) => typeof item === "string" && item.trim(),
      );

      return {
        type:
          typeof path?.type === "string" && path.type.trim()
            ? path.type.trim()
            : "role_edge_conflict",
        pathText,
        conflictLabels,
      };
    })
    .filter(Boolean);
}

function formatDoDirectRelations(doItem) {
  return safeArray(doItem?.direct_relations)
    .map((relation) => {
      const relationText =
        typeof relation?.relation_text === "string" &&
        relation.relation_text.trim()
          ? relation.relation_text.trim()
          : "";

      if (!relationText) return null;

      return {
        type:
          typeof relation?.type === "string" && relation.type.trim()
            ? relation.type.trim()
            : "direct_relation",
        relationText,
      };
    })
    .filter(Boolean);
}

function formatDoMediatedPaths(doItem) {
  return safeArray(doItem?.mediated_paths)
    .map((path) => {
      const pathText =
        typeof path?.path_text === "string" && path.path_text.trim()
          ? path.path_text.trim()
          : "";

      if (!pathText) return null;

      return {
        type:
          typeof path?.type === "string" && path.type.trim()
            ? path.type.trim()
            : "mediated_pathway",
        pathText,
      };
    })
    .filter(Boolean);
}

function formatDoDSeparationAudit(doItem) {
  const audit = doItem?.d_separation_audit;

  if (!audit || typeof audit !== "object") return null;

  const summary =
    typeof audit.summary === "string" && audit.summary.trim()
      ? audit.summary.trim()
      : "";

  if (!summary) return null;

  const status =
    typeof audit.status === "string" && audit.status.trim()
      ? audit.status.trim()
      : "not_applicable";

  const tooltip =
    typeof audit.tooltip === "string" && audit.tooltip.trim()
      ? audit.tooltip.trim()
      : "D-separation checks whether paths between X and Y are blocked or opened by conditioning. This is a graph-structural check, not empirical validation.";

  return {
    status,
    summary,
    tooltip,
    backdoorPathsChecked:
      typeof audit.backdoor_paths_checked === "number"
        ? audit.backdoor_paths_checked
        : 0,
    blockedBackdoorPaths:
      typeof audit.blocked_backdoor_paths === "number"
        ? audit.blocked_backdoor_paths
        : 0,
    openBackdoorPaths: safeArray(audit.open_backdoor_paths),
    colliderPathsOpened: safeArray(audit.collider_paths_opened),
  };
}

function dSeparationClassName(status) {
  if (status === "passed") {
    return "text-emerald-300";
  }

  if (status === "failed" || status === "warning") {
    return "text-amber-300";
  }

  return "text-slate-400";
}

function formatDoCautionStructures(doItem) {
  return safeArray(doItem?.caution_structures)
    .map((structure) => {
      const relations = safeArray(structure?.relations).filter(
        (value) => typeof value === "string" && value.trim(),
      );

      if (!relations.length) return null;

      const colliderLabels = safeArray(structure?.collider_labels).filter(
        (value) => typeof value === "string" && value.trim(),
      );

      return {
        type:
          typeof structure?.type === "string" && structure.type.trim()
            ? structure.type.trim()
            : "collider_caution_structure",
        relations,
        colliderLabels,
        colliderForm:
          typeof structure?.collider_form === "string" &&
          structure.collider_form.trim()
            ? structure.collider_form.trim()
            : "",
      };
    })
    .filter(Boolean);
}

function deriveDoPanelSummary(doItems, graph, causalModel) {
  const items = normaliseDoResults(doItems);

  const nodeLookup = buildNodeLookup(graph, causalModel);

  if (!items.length) {
    return {
      method: "no-graph",
      badge: "No graph available",
      estimands: [],
      body: "No confirmed causal graph was available for do-calculus assessment.",
      note: "This is a structural property of the confirmed graph, not a claim about empirical truth or real-world feasibility.",
    };
  }

  const methods = items.map((item) => inferDisplayDoMethod(item, nodeLookup));

  let summaryMethod = "unidentifiable";
  if (items.length === 1) {
    summaryMethod = methods[0] || "unidentifiable";
  } else if (methods.every((m) => m === "direct")) {
    summaryMethod = "direct";
  } else if (methods.some((m) => m === "identifiable_under_adjustment")) {
    summaryMethod = "identifiable_under_adjustment";
  } else if (methods.some((m) => m === "frontdoor")) {
    summaryMethod = "frontdoor";
  } else if (methods.every((m) => m && m !== "unidentifiable")) {
    summaryMethod = "identifiable";
  }

  const estimands = items.map((item) => ({
    id: `${item?.X || item?.intervention || "X"}-${item?.Y || "Y"}`,
    method: inferDisplayDoMethod(item, nodeLookup),
    label: formatDoEstimand(item),
    body: describeDoMethod(item, nodeLookup, graph),
    supportSet: describeDoSupportSet(item, nodeLookup, graph),
    dSeparationAudit: formatDoDSeparationAudit(item),
    directRelations: formatDoDirectRelations(item),
    mediatedPaths: formatDoMediatedPaths(item),
    conflictPaths: formatDoConflictPaths(item),
    cautionStructures: formatDoCautionStructures(item),
  }));

  const body =
    estimands.length === 1
      ? estimands[0].body
      : `The confirmed DAG yields ${estimands.length} intervention questions with different identification routes. The panels below show the structural status of each causal query separately.`;
  return {
    method: summaryMethod,
    badge: panelBadgeLabel(summaryMethod, estimands.length),
    badgeClassName: panelBadgeClasses(summaryMethod, estimands.length),
    estimands,
    body,
    note: "This is a structural property of the confirmed graph, not a claim about empirical truth, effect magnitude, or real-world feasibility",
  };
}

function deriveFallbackAudit({
  claimFrame,
  evidence,
  evidenceLinks,
  causalModel,
}) {
  const links = safeArray(evidenceLinks?.links);
  const linkByIndex = new Map();

  for (const link of links) {
    if (typeof link?.subclaim_index === "number") {
      linkByIndex.set(link.subclaim_index, link);
    }
  }

  const rawSubclaims = safeArray(claimFrame?.subclaims);
  const textSubclaims = rawSubclaims.map((text, idx) => {
    const link = linkByIndex.get(idx);
    return {
      id: `S${idx + 1}`,
      text: typeof text === "string" ? text : "—",
      support_count: link?.supporting_evidence_ids?.length || 0,
      counter_count: link?.counter_evidence_ids?.length || 0,
      neutral_count: link?.neutral_evidence_ids?.length || 0,
    };
  });

  const graphEdges = safeArray(causalModel?.edges);
  const graphRelations = graphEdges.map((edge, idx) => {
    const from = edge?.from ?? edge?.source ?? "—";
    const to = edge?.to ?? edge?.target ?? "—";
    return {
      id: `R${idx + 1}`,
      relation_text: `${from} → ${to}`,
      type: edge?.type || edge?.relation_kind || "directed_relation",
    };
  });

  const evidenceItems = safeArray(evidence?.evidence_items);
  const totalItems = evidenceItems.length;
  const supportItems = evidenceItems.filter(
    (e) => e?.role === "support",
  ).length;
  const counterItems = evidenceItems.filter(
    (e) => e?.role === "counter",
  ).length;
  const neutralItems = totalItems - supportItems - counterItems;
  const unlinkedEvidenceIds = safeArray(evidenceLinks?.unlinked_evidence_ids);
  const linkedItems = Math.max(0, totalItems - unlinkedEvidenceIds.length);

  return {
    status: "fallback",
    boundary_warning:
      "This audit layer is structural and linguistic. It does not provide empirical evidence for the graph or the confirmed causal structure.",
    interpretation_metadata: {
      category: claimFrame?.frame_type || "Unavailable",
      modality: claimFrame?.modality || "Unavailable",
      form: claimFrame?.surface_form || claimFrame?.frame_type || "Unavailable",
      flags: safeArray(claimFrame?.flags),
    },
    confirmed_graph_relations: graphRelations,
    text_subclaims: textSubclaims,
    evidence_overview: {
      total_items: totalItems,
      linked_items: linkedItems,
      unlinked_items: unlinkedEvidenceIds.length,
      support_items: supportItems,
      counter_items: counterItems,
      neutral_items: neutralItems,
    },
    evidence_items: [],
    unlinked_evidence_ids: unlinkedEvidenceIds,
    summary: totalItems
      ? `${textSubclaims.length} text-derived subclaim(s); ${totalItems} sentence-level evidence item(s); ${linkedItems} linked and ${unlinkedEvidenceIds.length} unlinked.`
      : "",
    notes:
      "Fallback audit view derived from existing claim, evidence, and linkage fields.",
  };
}

export default function PatResultPanel({
  pipelineResult,
  onJumpToConfirmedDag,
  reversalAcknowledged = false,
}) {
  window.__PAT_LAST_RESULT = pipelineResult;

  if (!pipelineResult || typeof pipelineResult !== "object") return null;

  const src =
    pipelineResult?.pipeline_result &&
    typeof pipelineResult.pipeline_result === "object"
      ? pipelineResult.pipeline_result
      : pipelineResult;

  const claimFrame = safeObject(
    src?.pat_claim_frame || pipelineResult?.pat_claim_frame,
  );
  const evidence = safeObject(
    src?.pat_evidence || pipelineResult?.pat_evidence,
  );
  const evidenceLinks = safeObject(
    src?.pat_evidence_links || pipelineResult?.pat_evidence_links,
  );
  const causalModel = safeObject(
    src?.pat_causal_model || pipelineResult?.pat_causal_model,
  );
  const bayes = safeObject(
    src?.pat_classic_bayes || pipelineResult?.pat_classic_bayes,
  );
  const patDo = safeObject(src?.pat_do || pipelineResult?.pat_do);
  const patRefine =
    src?.pat_causal_prompt || pipelineResult?.pat_causal_prompt || null;
  const actionLegitimacy = safeObject(
    src?.pat_action_legitimacy || pipelineResult?.pat_action_legitimacy,
  );

  const patEntryAudit = safeObject(
    src?.pat_entry_audit || pipelineResult?.pat_entry_audit,
  );

  const causalEntryLanguage = formatCausalEntryLanguage(patEntryAudit);
  const causalEntryTrigger = formatCausalEntryTrigger(patEntryAudit);

  const illustrativePathways = (() => {
    const raw =
      src?.pat_illustrative_pathways ??
      pipelineResult?.pat_illustrative_pathways ??
      pipelineResult?.raw?.pat_illustrative_pathways ??
      null;

    if (Array.isArray(raw)) return raw;
    if (Array.isArray(raw?.pathways)) return raw.pathways;
    return [];
  })();

  const audit = useMemo(() => {
    const preferred =
      src?.pat_claim_evidence_audit ||
      pipelineResult?.pat_claim_evidence_audit ||
      null;

    const preferredOriginalSemantic = safeObject(
      preferred?.original_semantic_interpretation,
    );

    const preferredHasContent =
      preferred &&
      typeof preferred === "object" &&
      (safeArray(preferred?.confirmed_graph_relations).length > 0 ||
        safeArray(preferred?.text_subclaims).length > 0 ||
        Object.keys(safeObject(preferred?.evidence_overview)).length > 0 ||
        formatDisplayValue(preferredOriginalSemantic?.X) !== "—" ||
        formatDisplayValue(preferredOriginalSemantic?.Y) !== "—");

    if (preferredHasContent) {
      return preferred;
    }

    return deriveFallbackAudit({
      claimFrame,
      evidence,
      evidenceLinks,
      causalModel,
    });
  }, [src, pipelineResult, claimFrame, evidence, evidenceLinks, causalModel]);

  const graph =
    src?.user_graph ||
    src?.confirmed_dag ||
    pipelineResult?.user_graph ||
    pipelineResult?.confirmed_dag;

  const unconnectedRoleAudit = normaliseUnconnectedRoleAudit(
    src,
    pipelineResult,
  );

  const roleBuckets = {
    X: [],
    M: [],
    Z: [],
    Y: null,
  };

  if (graph?.nodes && Array.isArray(graph.nodes)) {
    graph.nodes.forEach((node) => {
      if (!node || typeof node !== "object") return;

      if (node.role === "Y") {
        roleBuckets.Y = node;
      } else if (roleBuckets[node.role]) {
        roleBuckets[node.role].push(node);
      }
    });
  }

  const roleAwareBcsmNarrative =
    roleBuckets.Y &&
    `The committed model includes ${
      roleBuckets.X.length
        ? `${roleBuckets.X.length} candidate X variable${
            roleBuckets.X.length > 1 ? "s" : ""
          } (${roleBuckets.X.map((n) => `'${n.label}'`).join(", ")})`
        : ""
    }${
      roleBuckets.M.length
        ? `, ${roleBuckets.M.length} mediator${
            roleBuckets.M.length > 1 ? "s" : ""
          } (${roleBuckets.M.map((n) => `'${n.label}'`).join(", ")})`
        : ""
    }${
      roleBuckets.Z.length
        ? `, ${roleBuckets.Z.length} background or candidate confounder${
            roleBuckets.Z.length > 1 ? "s" : ""
          } (${roleBuckets.Z.map((n) => `'${n.label}'`).join(", ")})`
        : ""
    } with '${
      roleBuckets.Y.label
    }' as the outcome. The interpretation is weakly constrained by the graph and may change under plausible model extensions.`;

  const bcsmPosterior =
    typeof src?.pat_bcsm?.posterior === "number"
      ? src.pat_bcsm.posterior
      : typeof pipelineResult?.pat_bcsm?.posterior === "number"
        ? pipelineResult.pat_bcsm.posterior
        : null;

  const bcsm =
    typeof bcsmPosterior === "number" ? Math.round(bcsmPosterior * 100) : null;

  const bcsmCredence =
    safeObject(src?.pat_bcsm?.credence_scale) &&
    Object.keys(safeObject(src?.pat_bcsm?.credence_scale)).length > 0
      ? safeObject(src?.pat_bcsm?.credence_scale)
      : safeObject(pipelineResult?.pat_bcsm?.credence_scale);

  const bcsmBandLabel =
    typeof bcsmCredence?.label === "string" && bcsmCredence.label.trim()
      ? bcsmCredence.label.trim()
      : null;

  const bcsmBandDescription =
    typeof bcsmCredence?.interpretation === "string" &&
    bcsmCredence.interpretation.trim()
      ? bcsmCredence.interpretation.trim()
      : null;

  const coreProp =
    claimFrame.core_proposition || pipelineResult?.raw_input || "";

  const auditGraphRelations = safeArray(audit?.confirmed_graph_relations);
  const auditOverview = safeObject(audit?.evidence_overview);
  const auditSummary = audit?.summary || "";
  const auditNotes = audit?.notes || "";
  const auditBoundaryWarning = audit?.boundary_warning || "";

  const originalSemantic = safeObject(audit?.original_semantic_interpretation);
  const semanticComparison = safeObject(audit?.semantic_structure_comparison);

  const semanticSubclaims = formatSemanticSubclaims(
    originalSemantic?.subclaims,
  );

  const plainGraphRelations = formatRelationList(auditGraphRelations);

  const confirmedDagRelationsForPdf = plainGraphRelations.length
    ? plainGraphRelations
    : graphRelationFallback(graph);

  const directEvidenceOverview = deriveDirectEvidenceOverview(
    evidence,
    evidenceLinks,
  );

  const linkedCount = directEvidenceOverview.has_payload
    ? directEvidenceOverview.linked_items
    : (auditOverview.linked_items ?? 0);

  const unlinkedCount = directEvidenceOverview.has_payload
    ? directEvidenceOverview.unlinked_items
    : (auditOverview.unlinked_items ?? 0);

  const linkedSubclaimCount = directEvidenceOverview.has_payload
    ? directEvidenceOverview.linked_subclaims
    : 0;

  const totalEvidenceItems = directEvidenceOverview.has_payload
    ? directEvidenceOverview.total_items
    : (auditOverview.total_items ?? 0);

  const hasAnyEvidence = directEvidenceOverview.has_payload
    ? totalEvidenceItems > 0
    : linkedCount > 0 || unlinkedCount > 0;

  const retainedFromWordingRaw = formatSemanticSubclaims(
    semanticComparison?.retained_from_wording,
  );
  const notRetainedFromWordingRaw = formatSemanticSubclaims(
    semanticComparison?.not_retained_from_wording,
  );
  const structuralNotes = semanticComparison?.structural_notes || "";

  const rawInputText =
    pipelineResult?.raw_input ||
    src?.raw_input ||
    pipelineResult?.pipeline_result?.raw_input ||
    "";

  const confirmedSubclaims = formatSemanticSubclaims(
    audit?.confirmed_structural_interpretation?.subclaims ||
      claimFrame?.subclaims,
  );

  const graphRelationsForRetention = confirmedDagRelationsForPdf.length
    ? confirmedDagRelationsForPdf
    : graphRelationFallback(graph);

  const wholePromptMarkedNotRetained =
    notRetainedFromWordingRaw.length === 1 &&
    normaliseForComparison(notRetainedFromWordingRaw[0]) ===
      normaliseForComparison(rawInputText);

  const excessiveNotRetainedList =
    rawInputText &&
    notRetainedFromWordingRaw.length > 1 &&
    notRetainedFromWordingRaw.join(" ").length >= rawInputText.length * 0.75;

  const retainedFromWording =
    wholePromptMarkedNotRetained || excessiveNotRetainedList
      ? graphRelationsForRetention
      : retainedFromWordingRaw;

  const notRetainedFromWording =
    wholePromptMarkedNotRetained || excessiveNotRetainedList
      ? defaultNotRepresentedByDagItems()
      : notRetainedFromWordingRaw;

  const semanticDirection = safeObject(semanticComparison?.semantic_direction);
  const confirmedDirection = safeObject(
    semanticComparison?.confirmed_direction,
  );

  const semanticEndpoint = normaliseForComparison(
    semanticDirection?.Y || semanticDirection?.target || "",
  );

  const confirmedEndpoint = normaliseForComparison(
    confirmedDirection?.Y ||
      confirmedDirection?.target ||
      roleBuckets?.Y?.label ||
      "",
  );

  const endpointChanged =
    Boolean(semanticEndpoint) &&
    Boolean(confirmedEndpoint) &&
    semanticEndpoint !== confirmedEndpoint;

  const directionReversed =
    Boolean(semanticComparison?.direction_reversed) && endpointChanged;

  const pathwayStructureChanged =
    Boolean(semanticComparison?.direction_reversed) && !endpointChanged;

  const reversalWarning =
    audit?.reversal_warning || semanticComparison?.reversal_warning || "";

  const semanticMatchesGraph = arraysBroadlyAlign(
    semanticSubclaims,
    plainGraphRelations,
  );
  const retainedMatchesSemantic = arraysBroadlyAlign(
    retainedFromWording,
    semanticSubclaims,
  );
  const showSemanticSubclaimList =
    semanticSubclaims.length > 0 && !semanticMatchesGraph;
  const showRetainedList =
    retainedFromWording.length > 0 &&
    (!semanticMatchesGraph || !retainedMatchesSemantic);
  const showExpandedComparison =
    directionReversed ||
    pathwayStructureChanged ||
    notRetainedFromWording.length > 0 ||
    showRetainedList;

  const apparentCauseValue =
    formatDisplayValue(originalSemantic?.X) !== "—"
      ? originalSemantic?.X
      : claimFrame?.X;

  const apparentOutcomeValue =
    formatDisplayValue(originalSemantic?.Y) !== "—"
      ? originalSemantic?.Y
      : claimFrame?.Y;

  const confirmedOutcomeLabel = confirmedOutcomeLabelFromGraph(
    graph,
    formatDisplayValue(apparentOutcomeValue),
  );

  const outcomeLabelForAudit = confirmedOutcomeLabel;
  const semanticSummaryText = showSemanticSubclaimList
    ? "PAT interpreted the wording as having a causal form with additional semantic detail beyond the final confirmed graph."
    : "PAT interpreted this as a causal claim linking the apparent cause to the nominated outcome.";

  const graphSummaryText = describeGraphSummary(
    auditGraphRelations,
    outcomeLabelForAudit,
  );

  const comparisonSummaryText = directionReversed
    ? reversalAcknowledged
      ? "The confirmed DAG changes the endpoint direction of PAT's original semantic reading, and that divergence has been acknowledged."
      : "The confirmed DAG changes the endpoint direction of PAT's original semantic reading and requires explicit acknowledgement."
    : pathwayStructureChanged
      ? "The confirmed DAG preserves the focal outcome while retaining, omitting, or reorganising intermediate pathway variables."
      : showExpandedComparison
        ? "The confirmed DAG sharpens the wording by forcing an explicit causal structure."
        : "The confirmed DAG broadly retains PAT's original semantic reading of the wording.";

  const evidenceSummaryText =
    totalEvidenceItems > 0
      ? linkedCount > 0
        ? `PAT linked ${linkedCount} sentence-level evidence item${linkedCount === 1 ? "" : "s"} across ${linkedSubclaimCount} subclaim${linkedSubclaimCount === 1 ? "" : "s"} and left ${unlinkedCount} unlinked.`
        : `PAT extracted ${totalEvidenceItems} sentence-level evidence item${totalEvidenceItems === 1 ? "" : "s"}; however, none were linked to the confirmed claim structure.`
      : "No sentence-level evidence items were available in this case.";
  const bayesClaim =
    Array.isArray(bayes.claims) && bayes.claims.length > 0
      ? bayes.claims[0]
      : null;

  const prior = bayesClaim?.prior;
  const posterior = bayesClaim?.posterior;
  const bayesNotes = bayes.notes || "";
  const bayesExplanation =
    typeof prior === "number" && typeof posterior === "number"
      ? deriveBayesExplanation(prior, posterior)
      : null;

  const doPanel = deriveDoPanelSummary(patDo, graph, causalModel);

  const actionScope =
    actionLegitimacy?.intervention_scope &&
    typeof actionLegitimacy.intervention_scope === "object"
      ? actionLegitimacy.intervention_scope
      : null;

  const causalIdentifiability =
    actionLegitimacy?.causal_identifiability &&
    typeof actionLegitimacy.causal_identifiability === "object"
      ? actionLegitimacy.causal_identifiability
      : null;

  const actionLegitimacyText = [
    `Intervention scope: ${
      actionScope?.label ||
      actionLegitimacy?.verdict ||
      actionLegitimacy?.status ||
      "Not assessed"
    }`,
    actionScope?.rationale ||
      "The confirmed causal structure supports intervention reasoning only within the limits of the confirmed graph and stated assumptions.",
    `Causal identifiability: ${doPanel.badge || causalIdentifiability?.label || "Not assessed"}`,
    doPanel.body ||
      causalIdentifiability?.rationale ||
      "No additional identifiability rationale was available.",
    doPanel.method === "frontdoor"
      ? "How to interpret this result: Read this as support for pathway-based structural reasoning within the confirmed DAG. The model can be used to examine how X may relate to Y through authorised mediator/pathway structure and, where present, alongside a direct X → Y edge. It should not be read as a Pearl front-door identification result, evidence that the intervention will work, proof that the mediator explains the effect, or empirical validation of the pathway."
      : "How to interpret this result: Use this model to clarify assumptions, compare scenarios, and explore possible causal pathways. Do not treat it as evidence that an intervention will succeed without independent empirical support.",
    "Boundary: This assessment does not establish empirical truth, effect magnitude, policy correctness, or real-world feasibility.",
  ];

  const originalClaimFrame = safeObject(
    src?.pat_original_claim_frame ||
      pipelineResult?.pat_original_claim_frame ||
      audit?.original_claim_frame,
  );

  const confirmedClaimFrame = safeObject(
    src?.pat_confirmed_claim_frame ||
      pipelineResult?.pat_confirmed_claim_frame ||
      audit?.confirmed_claim_frame ||
      claimFrame,
  );

  const variablesForPreDagAudit = safeObject(
    src?.pat_variables || pipelineResult?.pat_variables,
  );

  const pipelineRunIdForPdf =
    src?.pipeline_run_id ||
    src?.run_id ||
    pipelineResult?.pipeline_run_id ||
    pipelineResult?.run_id ||
    "—";

  const preDagAuditTrail = buildPreDagAuditTrail({
    rawInputText: rawInputText || coreProp || "",
    originalSemantic,
    originalClaimFrame,
    claimFrame,
    confirmedClaimFrame,
    variables: variablesForPreDagAudit,
    confirmedDagRelations: confirmedDagRelationsForPdf,
    unconnectedRoleAudit,
    pipelineRunId: pipelineRunIdForPdf,
  });

  if (causalEntryTrigger || causalEntryLanguage.length > 0) {
    const triggerLine = causalEntryTrigger
      ? `PAT causal-entry trigger: ${causalEntryTrigger}.`
      : "";

    const languageLine = causalEntryLanguage.length
      ? `Detected causal-entry language: ${causalEntryLanguage.join("; ")}.`
      : "";

    preDagAuditTrail.splice(
      1,
      0,
      [
        triggerLine,
        languageLine,
        "These terms explain PAT routing only. They do not assign causal roles or establish a causal claim.",
      ]
        .filter(Boolean)
        .join(" "),
    );
  }

  const postDagReport = useMemo(
    () => ({
      generatedAt: new Date().toLocaleString(),
      pipelineRunId: pipelineRunIdForPdf,
      footerText:
        "PAT v4.0-20260513 | UI v5.0-20260513 | Post-DAG causal audit report",
      rawInputText: rawInputText || coreProp || "—",
      preDagAuditTrail,

      causalEntryTrigger,
      causalEntryLanguage,

      intervention: {
        method: doPanel.method || "",
        badge: doPanel.badge || "—",
        body: doPanel.body || "—",
        estimands: doPanel.estimands || [],
        note: doPanel.note || "—",
      },

      bcsm: {
        percent: bcsm !== null ? `${bcsm}%` : "—",
        bandLabel: bcsmBandLabel || "—",
        bandDescription: bcsmBandDescription || "—",
        roleAwareNarrative: roleAwareBcsmNarrative || "—",
        calibrationNote: bcsmCalibrationNoteText(),
      },

      actionLegitimacyText,

      audit: {
        apparentCause: cleanAuditDisplayText(apparentCauseValue),
        apparentOutcome: cleanAuditDisplayText(apparentOutcomeValue),
        confirmedOutcome: cleanAuditDisplayText(confirmedOutcomeLabel),
        semanticSummaryText,
        graphSummaryText,
        comparisonSummaryText,
        evidenceSummaryText,
        confirmedDagRelations: confirmedDagRelationsForPdf,
        semanticSubclaims,
        retainedFromWording,
        notRetainedFromWording,
        evidenceItems: buildPdfEvidenceItems(evidence, evidenceLinks, audit),
        evidenceFooterNote:
          "The original semantic interpretation reflects PAT's reading of the input wording. The confirmed structural interpretation reflects the causal model authorised by the user. Sentence-level input-support links are heuristic and structural, not empirical evidence. Where the semantic reading and confirmed DAG diverge, PAT should surface that divergence explicitly rather than treating it as invisible drift.",
        auditBoundaryWarning,
        auditNotes,
        auditSummary,
      },

      unconnectedRoleAudit,

      bayes: {
        statement: bayesClaim?.statement || coreProp || "—",
        prior: formatProb(prior),
        posterior: formatProb(posterior),
        label: bayesCredenceLabel(posterior),
        explanation: bayesExplanation || "—",
        notes:
          bayesNotes ||
          "Method note: this is a reflective scoring layer over the user’s language. It does not alter the confirmed causal model.",
      },

      pathways: illustrativePathways,
    }),
    [
      rawInputText,
      coreProp,
      pipelineRunIdForPdf,
      doPanel,
      bcsm,
      bcsmBandLabel,
      bcsmBandDescription,
      roleAwareBcsmNarrative,
      actionLegitimacyText,
      apparentCauseValue,
      apparentOutcomeValue,
      confirmedOutcomeLabel,
      semanticSummaryText,
      graphSummaryText,
      comparisonSummaryText,
      evidenceSummaryText,
      confirmedDagRelationsForPdf,
      semanticSubclaims,
      evidence,
      evidenceLinks,
      audit,
      retainedFromWording,
      notRetainedFromWording,
      auditBoundaryWarning,
      auditNotes,
      auditSummary,
      bayesClaim,
      prior,
      posterior,
      bayesExplanation,
      bayesNotes,
      illustrativePathways,
      preDagAuditTrail,
      causalEntryTrigger,
      causalEntryLanguage,
    ],
  );

  const [showBayes, setShowBayes] = useState(false);
  const [showAudit, setShowAudit] = useState(false);
  const [isDownloadingPdf, setIsDownloadingPdf] = useState(false);

  return (
    <section className="mt-6 mb-6 rounded-2xl border border-slate-700/80 bg-slate-950/80 p-4 text-xs text-slate-100 space-y-6">
      <header className="flex flex-col items-center gap-3">
        <h3 className="text-2xl font-semibold text-emerald-300 tracking-wide">
          Causal Model Evaluation and Intervention Scope
        </h3>

        <button
          type="button"
          disabled={isDownloadingPdf}
          onClick={() => {
            if (isDownloadingPdf) return;

            setIsDownloadingPdf(true);

            try {
              const reportSnapshot =
                typeof structuredClone === "function"
                  ? structuredClone(postDagReport)
                  : JSON.parse(JSON.stringify(postDagReport));

              console.log("[PAT PDF] clicked:", {
                pipelineRunId: reportSnapshot?.pipelineRunId,
                generatedAt: reportSnapshot?.generatedAt,
              });

              downloadPostDagReportAsPDF(reportSnapshot);
            } catch (error) {
              console.error("[PAT PDF] generation failed:", error);
              window.alert(
                "PAT report PDF generation failed. See console for details.",
              );
            } finally {
              window.setTimeout(() => {
                setIsDownloadingPdf(false);
              }, 1500);
            }
          }}
          className={`rounded-lg border border-emerald-600/70 bg-emerald-900/30 px-4 py-2 text-sm font-medium text-emerald-200 hover:bg-emerald-800/40 transition-all duration-300 ${
            isDownloadingPdf ? "opacity-60 cursor-not-allowed" : ""
          }`}
        >
          {isDownloadingPdf ? "Preparing PDF…" : "Download PAT Report"}
        </button>
      </header>

      <div className="rounded-lg border border-slate-700/70 bg-slate-900/70 p-4 space-y-4">
        <h4 className="text-lg font-semibold text-amber-300">
          Intervention Identifiability (do-calculus)
        </h4>

        <div
          className={`inline-flex items-center rounded-full border px-3 py-1 text-xs ${
            doPanel.badgeClassName || methodBadgeClasses(doPanel.method)
          }`}
        >
          {doPanel.badge}
          {doPanel.method === "direct" && <InfoFlag label={directInfoText()} />}
          {doPanel.method === "frontdoor" && (
            <InfoFlag label={mediatedPathwayInfoText()} />
          )}
          {doPanel.method === "identifiable_under_adjustment" && (
            <InfoFlag label={adjustmentInfoText()} />
          )}
          {doPanel.method === "collider_caution" && (
            <InfoFlag label={colliderInfoText()} />
          )}
          {doPanel.method === "role_edge_conflict" && (
            <InfoFlag label={roleEdgeConflictInfoText()} />
          )}
        </div>

        <p className="text-sm text-slate-300 leading-snug">{doPanel.body}</p>

        {doPanel.estimands.length > 0 && (
          <div className="space-y-3">
            {doPanel.estimands.map((item) => (
              <div
                key={item.id}
                className="rounded-lg border border-slate-700/60 bg-slate-950/30 p-3 space-y-2"
              >
                <div className="text-sm text-slate-100 font-medium">
                  {item.label}
                </div>

                {doPanel.estimands.length > 1 && (
                  <p className="text-sm text-slate-400 leading-snug">
                    {item.body}
                    {item.supportSet?.label === "Pathway variables" && (
                      <InfoFlag label={mediatedPathwayInfoText()} />
                    )}
                    {item.supportSet?.label ===
                      "Candidate confounder adjustment set" && (
                      <InfoFlag label={adjustmentInfoText()} />
                    )}
                  </p>
                )}

                {item.supportSet && (
                  <div className="space-y-1">
                    <p className="text-[0.78rem] text-slate-400 leading-snug">
                      {item.supportSet.label}: {item.supportSet.value}
                    </p>

                    {item.supportSet.note && (
                      <p className="text-[0.72rem] text-amber-300 leading-snug">
                        {item.supportSet.note}
                      </p>
                    )}
                  </div>
                )}

                {item.dSeparationAudit && (
                  <p
                    className={`text-[0.72rem] leading-snug ${dSeparationClassName(
                      item.dSeparationAudit.status,
                    )}`}
                  >
                    {item.dSeparationAudit.summary}
                    <InfoFlag label={item.dSeparationAudit.tooltip} />
                  </p>
                )}

                {item.directRelations?.length > 0 && (
                  <div className="rounded-lg border border-emerald-600/50 bg-emerald-950/20 p-3 space-y-2">
                    <div className="text-[0.78rem] font-semibold text-emerald-200">
                      Authorised direct relation
                    </div>

                    {item.directRelations.map((relation, index) => (
                      <p
                        key={`${item.id}-direct-relation-${index}`}
                        className="text-[0.78rem] text-slate-200 leading-snug"
                      >
                        {relation.relationText}
                      </p>
                    ))}
                  </div>
                )}

                {item.mediatedPaths?.length > 0 && (
                  <div className="rounded-lg border border-emerald-600/50 bg-emerald-950/20 p-3 space-y-2">
                    <div className="text-[0.78rem] font-semibold text-emerald-200">
                      {item.method === "frontdoor"
                        ? "Authorised mediated pathways"
                        : "Additional authorised pathway structure"}
                    </div>

                    {item.mediatedPaths.map((path, index) => (
                      <p
                        key={`${item.id}-mediated-path-${index}`}
                        className="text-[0.78rem] text-slate-200 leading-snug"
                      >
                        {path.pathText}
                      </p>
                    ))}

                    {item.method !== "frontdoor" && (
                      <p className="text-[0.72rem] text-amber-300 leading-snug">
                        This pathway is present in the confirmed DAG, but it is
                        not being treated as a front-door identification result.
                      </p>
                    )}
                  </div>
                )}

                {item.conflictPaths?.length > 0 && (
                  <div className="rounded-lg border border-amber-600/50 bg-amber-950/20 p-3 space-y-2">
                    <div className="text-[0.78rem] font-semibold text-amber-200">
                      Conflict pathway
                    </div>

                    {item.conflictPaths.map((path, index) => (
                      <div
                        key={`${item.id}-conflict-path-${index}`}
                        className="space-y-1"
                      >
                        <p className="text-[0.78rem] text-slate-200 leading-snug">
                          {path.pathText}
                        </p>

                        {path.conflictLabels.length > 0 && (
                          <p className="text-[0.72rem] text-amber-300 leading-snug">
                            Role conflict variable
                            {path.conflictLabels.length === 1 ? "" : "s"}:{" "}
                            {path.conflictLabels.join(", ")}
                          </p>
                        )}
                      </div>
                    ))}
                  </div>
                )}

                {item.cautionStructures?.length > 0 && (
                  <div className="rounded-lg border border-amber-600/50 bg-amber-950/20 p-3 space-y-2">
                    <div className="text-[0.78rem] font-semibold text-amber-200">
                      Implicated DAG structure
                    </div>

                    {item.cautionStructures.map((structure, index) => (
                      <div
                        key={`${item.id}-caution-structure-${index}`}
                        className="space-y-2"
                      >
                        <div className="space-y-1">
                          {structure.relations.map(
                            (relation, relationIndex) => (
                              <p
                                key={`${item.id}-caution-relation-${index}-${relationIndex}`}
                                className="text-[0.78rem] text-slate-200 leading-snug"
                              >
                                {relation}
                              </p>
                            ),
                          )}
                        </div>

                        {structure.colliderForm && (
                          <p className="text-[0.72rem] text-amber-200 leading-snug">
                            Collider form: {structure.colliderForm}
                          </p>
                        )}

                        {structure.colliderLabels.length > 0 && (
                          <p className="text-[0.72rem] text-amber-300 leading-snug">
                            Collider-risk variable
                            {structure.colliderLabels.length === 1
                              ? ""
                              : "s"}: {structure.colliderLabels.join(", ")}
                          </p>
                        )}
                      </div>
                    ))}
                  </div>
                )}
              </div>
            ))}
          </div>
        )}

        <p className="text-[0.75rem] text-slate-500 leading-snug">
          {doPanel.note}
        </p>
      </div>

      <div className="rounded-lg border border-slate-700/70 bg-slate-900/70 p-4">
        <div className="text-lg font-semibold text-amber-300 mb-2">
          Bayesian Causal Stability Metric (BCSM)
          {roleBuckets.Z.length > 0 && (
            <InfoFlag label="Z variables are candidate background or confounding variables. They do not automatically authorise adjustment unless the confirmed DAG supports that interpretation." />
          )}
        </div>

        <p className="text-sm text-slate-400 leading-snug mb-3">
          The BCSM summarises how structurally robust the committed causal
          interpretation remains across plausible model variations and
          assumptions.
        </p>

        <div className="text-2xl font-semibold text-white mb-1">
          {bcsm !== null ? `${bcsm}%` : "–"}
        </div>

        {bcsmBandLabel && (
          <p className="text-sm text-slate-200 font-medium">{bcsmBandLabel}</p>
        )}

        {bcsmBandDescription && (
          <p className="text-sm text-slate-400 leading-snug mt-1">
            {bcsmBandDescription}
          </p>
        )}

        {roleAwareBcsmNarrative && (
          <p className="text-sm text-slate-300 leading-snug mt-3">
            {roleAwareBcsmNarrative}
          </p>
        )}
      </div>

      <div className="mt-4">
        <p className="text-[0.72rem] leading-snug text-slate-500">
          {bcsmCalibrationNoteText()}
        </p>

        <div className="mt-5 border-t border-slate-700/70" />
      </div>

      {actionLegitimacy?.status || actionLegitimacy?.verdict ? (
        <PatActionLegitimacyPanel
          actionLegitimacy={actionLegitimacy}
          doPanel={doPanel}
        />
      ) : null}

      {patRefine?.detected && (
        <div className="p-3 rounded bg-amber-900/60 border-l-4 border-amber-400 text-[0.75rem]">
          <div className="text-amber-300 font-semibold mb-1">
            PAT vector-triggered analysis
          </div>
          <div className="text-amber-200">
            {patRefine.message || patRefine.text || patRefine.prompt}
          </div>
        </div>
      )}

      <div className="space-y-3">
        <div
          id="pat-claim-evidence-audit"
          className="rounded-lg border border-slate-700/70 bg-slate-900/70"
          style={{ scrollMarginTop: "40px" }}
        >
          <button
            onClick={() => setShowAudit((value) => !value)}
            className="w-full flex justify-between px-4 py-3"
          >
            <span className="text-lg font-semibold text-indigo-300">
              Claim-form and structural audit
              <InfoFlag label="Input-support links show how sentence-level wording maps to the confirmed structure. They are heuristic and structural, not empirical evidence for the confirmed causal structure." />
            </span>
            <span className="text-xs text-slate-400">
              {showAudit ? "Hide" : "Show"}
            </span>
          </button>

          {showAudit && (
            <div className="px-4 pb-4 space-y-5">
              <p className="text-sm text-slate-300 leading-snug">
                This panel contrasts PAT's original semantic reading of your
                wording with the causal structure you ultimately confirmed in
                the DAG.
              </p>

              {(causalEntryTrigger || causalEntryLanguage.length > 0) && (
                <div className="rounded-lg border border-slate-700/70 bg-slate-950/40 p-3 space-y-2">
                  <div className="text-sm font-semibold text-sky-300">
                    Causal-entry trigger
                  </div>

                  {causalEntryTrigger && (
                    <p className="text-sm text-slate-300 leading-snug">
                      PAT causal-entry trigger:{" "}
                      <span className="text-slate-100 font-medium">
                        {causalEntryTrigger}
                      </span>
                      .
                    </p>
                  )}

                  {causalEntryLanguage.length > 0 && (
                    <p className="text-sm text-slate-300 leading-snug">
                      Detected causal-entry language:{" "}
                      <span className="text-slate-100 font-medium">
                        {causalEntryLanguage.join("; ")}
                      </span>
                      .
                    </p>
                  )}

                  <p className="text-[0.75rem] text-slate-500 leading-snug">
                    These terms explain PAT routing only. They do not assign
                    causal roles or establish a causal claim.
                  </p>
                </div>
              )}

              <div className="space-y-3">
                <div className="rounded-lg border border-indigo-500/60 bg-slate-950/40 p-4 space-y-3">
                  <div className="text-sm font-semibold text-indigo-300">
                    PAT’s initial semantic reading
                  </div>

                  <p className="text-sm text-slate-300 leading-snug">
                    Apparent cause:{" "}
                    <span className="text-slate-100 font-medium">
                      {cleanAuditDisplayText(apparentCauseValue)}
                    </span>
                  </p>

                  <p className="text-sm text-slate-300 leading-snug">
                    User-nominated outcome:{" "}
                    <span className="text-slate-100 font-medium">
                      {cleanAuditDisplayText(apparentOutcomeValue)}
                    </span>
                  </p>

                  <p className="text-sm text-slate-400 leading-snug">
                    {semanticSummaryText}
                  </p>

                  {showSemanticSubclaimList && (
                    <div className="space-y-2 pt-1">
                      {semanticSubclaims.map((text, idx) => (
                        <div
                          key={`semantic-subclaim-${idx}`}
                          className="border border-slate-700/60 p-2 rounded"
                        >
                          <div className="text-sm text-slate-100">- {text}</div>
                        </div>
                      ))}
                    </div>
                  )}
                </div>

                <div className="flex justify-center text-slate-400 text-lg">
                  ↓
                </div>

                <div className="rounded-lg border border-sky-500/60 bg-slate-950/40 p-4 space-y-3">
                  <div className="text-sm font-semibold text-sky-300">
                    Confirmed DAG
                  </div>

                  <p className="text-sm text-slate-300 leading-snug">
                    {graphSummaryText}
                  </p>

                  {unconnectedRoleAudit.hasUnconnected && (
                    <div className="rounded-lg border border-amber-500/50 bg-amber-950/20 p-3 space-y-2">
                      <div className="text-sm font-semibold text-amber-300">
                        Role-authorised with no edge connection
                        <InfoFlag label={unconnectedRoleAuditInfoText()} />
                      </div>

                      <p className="text-sm text-slate-300 leading-snug">
                        {unconnectedRoleAudit.summary}
                      </p>

                      <div className="flex flex-wrap gap-2">
                        {unconnectedRoleAudit.variables.map((item) => (
                          <span
                            key={`${item.role}-${item.id || item.label}`}
                            className="inline-flex rounded-full border border-amber-600/60 bg-amber-900/20 px-2 py-1 text-[0.72rem] text-amber-100"
                          >
                            {item.label} · {formatRoleLabel(item.role)}
                          </span>
                        ))}
                      </div>
                    </div>
                  )}

                  {typeof onJumpToConfirmedDag === "function" && (
                    <button
                      type="button"
                      onClick={onJumpToConfirmedDag}
                      className="px-4 py-2 rounded border border-sky-500/60 text-sky-300 hover:bg-sky-950/40 hover:text-sky-200 transition-all duration-300"
                    >
                      Inspect confirmed DAG
                    </button>
                  )}
                </div>

                <div className="flex justify-center text-slate-400 text-lg">
                  ↓
                </div>

                <div className="rounded-lg border border-emerald-500/60 bg-slate-950/40 p-4 space-y-3">
                  <div className="text-sm font-semibold text-emerald-300">
                    Structural comparison
                  </div>

                  <p className="text-sm text-slate-300 leading-snug">
                    {comparisonSummaryText}
                  </p>

                  {(directionReversed || pathwayStructureChanged) && (
                    <div className="rounded-lg border border-amber-500/40 bg-amber-950/20 p-4 space-y-3">
                      <div className="text-sm font-semibold text-amber-300">
                        {directionReversed
                          ? reversalAcknowledged
                            ? "Semantic direction change acknowledged"
                            : "Semantic direction change detected"
                          : "Semantic pathway-structure change detected"}
                      </div>

                      <p className="text-sm text-slate-200 leading-snug">
                        {directionReversed
                          ? reversalWarning ||
                            "The confirmed DAG changes PAT's original semantic X → Y endpoint direction."
                          : "The confirmed DAG retains the endpoint outcome, but changes which intermediate pathways are represented."}
                      </p>

                      <div className="text-sm text-slate-300 leading-snug space-y-1">
                        <p>
                          Original semantic direction:{" "}
                          <span className="text-slate-100 font-medium">
                            {semanticDirection?.text || "—"}
                          </span>
                        </p>
                        <p>
                          Confirmed structural direction:{" "}
                          <span className="text-slate-100 font-medium">
                            {confirmedDirection?.text || "—"}
                          </span>
                        </p>
                      </div>

                      <p className="text-[0.75rem] text-slate-400 leading-snug">
                        {directionReversed
                          ? reversalAcknowledged
                            ? "You confirmed that the final X → Y configuration should remain as represented in the DAG."
                            : "A separate acknowledgement prompt is shown above the audit panel before interpretation proceeds."
                          : "This is an audit notice only. The pathway difference is recorded without treating it as an endpoint reversal."}
                      </p>
                    </div>
                  )}

                  {showExpandedComparison && (
                    <div className="space-y-2 pt-1">
                      {showRetainedList && (
                        <div className="space-y-2">
                          <div className="text-sm text-slate-200">
                            Retained from the wording
                          </div>

                          {retainedFromWording.map((text, idx) => (
                            <div
                              key={`retained-${idx}`}
                              className="border border-slate-700/60 p-2 rounded"
                            >
                              <div className="text-sm text-slate-100">
                                - {text}
                              </div>
                            </div>
                          ))}
                        </div>
                      )}

                      {notRetainedFromWording.length > 0 && (
                        <div className="space-y-2">
                          <div className="text-sm text-slate-200">
                            Not retained or not directly represented in the DAG
                          </div>

                          {notRetainedFromWording.map((text, idx) => (
                            <div
                              key={`not-retained-${idx}`}
                              className="border border-slate-700/60 p-2 rounded"
                            >
                              <div className="text-sm text-slate-100">
                                - {text}
                              </div>
                            </div>
                          ))}
                        </div>
                      )}
                    </div>
                  )}
                </div>
              </div>

              <div className="rounded-lg border border-slate-600/80 bg-slate-950/30 p-4 space-y-3">
                <div className="text-sm font-semibold text-slate-200">
                  Input-support linkage
                </div>

                <p className="text-sm text-slate-300 leading-snug">
                  {evidenceSummaryText}
                </p>

                {totalEvidenceItems > 0 && (
                  <div className="space-y-2 pt-1">
                    <PATEvidencePanel
                      evidence={evidence}
                      links={evidenceLinks}
                      audit={audit}
                    />
                  </div>
                )}
              </div>

              <p className="text-sm text-slate-300 leading-snug">
                We are not claiming this structure is empirically true.
              </p>

              <p className="text-sm text-slate-300 leading-snug">
                Therefore this panel shows the relationship between PAT's
                original reading of the wording and the causal structure you
                ultimately confirmed.
              </p>

              {(auditBoundaryWarning || auditNotes || auditSummary) && (
                <p className="text-[0.75rem] text-slate-400 leading-snug">
                  {auditBoundaryWarning || auditNotes || auditSummary}
                </p>
              )}
            </div>
          )}
        </div>
      </div>
      <div className="rounded-lg border border-slate-700/70 bg-slate-900/70">
        <button
          onClick={() => setShowBayes((value) => !value)}
          className="w-full flex justify-between px-4 py-3"
        >
          <div className="flex flex-col text-left">
            <span className="text-lg font-semibold text-indigo-300">
              User input causal form and assertion strength
            </span>
            <span className="text-[0.75rem] text-slate-400 leading-snug">
              This panel separates causal form from assertion strength. A
              question may have causal form while remaining interrogative rather
              than assertive.
            </span>
          </div>
          <span className="text-xs text-slate-400">
            {showBayes ? "Hide" : "Show"}
          </span>
        </button>

        {showBayes && (
          <div className="px-4 pb-4">
            {bayesClaim ? (
              <>
                <p className="text-sm text-slate-300 mb-3">
                  Input wording:{" "}
                  <span className="text-slate-100">
                    {bayesClaim.statement || coreProp}
                  </span>
                </p>

                <div className="flex flex-wrap gap-3 mb-4">
                  <div
                    className={`inline-flex items-center gap-2 rounded-full border px-3 py-1 text-xs ${bayesCredenceClasses(prior)}`}
                  >
                    <span className="font-medium">
                      Starting assertion strength
                    </span>
                    <span>{formatProb(prior)}</span>
                  </div>

                  <div
                    className={`inline-flex items-center gap-2 rounded-full border px-3 py-1 text-xs ${bayesCredenceClasses(posterior)}`}
                  >
                    <span className="font-medium">
                      Adjusted assertion strength
                    </span>
                    <span>{formatProb(posterior)}</span>
                  </div>

                  <div
                    className={`inline-flex items-center rounded-full border px-3 py-1 text-xs ${bayesCredenceClasses(posterior)}`}
                  >
                    {bayesCredenceLabel(posterior)}
                  </div>
                </div>

                {bayesExplanation && (
                  <p className="text-sm text-slate-200 leading-snug mb-2">
                    <span className="font-medium">Assessment: </span>
                    {bayesExplanation}
                  </p>
                )}

                <p className="text-sm text-slate-300 leading-snug mb-2">
                  This is a wording-level assessment only. It may indicate
                  causal form; however, it does not establish causal validity,
                  empirical support, or suitability for action.
                </p>

                <p className="text-[0.7rem] text-slate-500 italic mt-2">
                  {bayesNotes ||
                    "Method note: this is a reflective scoring layer over the user’s language. It does not alter the confirmed causal model."}
                </p>
              </>
            ) : (
              <p className="text-[0.75rem] text-slate-400">
                No Bayes signal available.
              </p>
            )}
          </div>
        )}
      </div>

      <PatIllustrativePathwaysPanel
        pathways={illustrativePathways}
        doPanel={doPanel}
      />
    </section>
  );
}
